import { makeObservable, observable, action, runInAction } from "mobx";
import OpsAnalyticsDashboardAPI from "../api/OpsAnalyticsDashboardAPI";
import {
  LabelWiseInventoryOpsDetails,
  OpsAnalyticsData,
  OrdersList,
} from "../models/Dashboard/OpsDashboard/OpsAnalytics";
import { OpsAnalyticsAPIFilters } from "../models/Dashboard/OpsDashboard/OpsAnalyticsFilters";
import {
  AnalyticsGraphResponse,
  AppLineChartData,
  LabelValue,
} from "../models/Dashboard/Analytics";
import { parseAnalyticsGraphDataToAppLineChartData } from "../views/components/charts/AppLineChart";
import { parseAnalyticsGraphDataToAppAreaBarChartData } from "../views/components/charts/AppAreaBarChart";
import { PaginatedAPIResponse } from "../models/NetworkResponse";
import dayjs from "dayjs";
import { parseAnalyticsGraphDataToAppStackChartData } from "../views/components/charts/AppStackChart";

export class OpsAnalyticsDashboardStore {
  analyticsAPI: OpsAnalyticsDashboardAPI;
  response: OpsAnalyticsData | null;
  mockup_tats: LabelValue[];
  ops_tats: LabelValue[];
  inward_data: LabelValue[];
  prpo_tats: LabelValue[];
  order_list_table: OrdersList[];
  dispatch_delivery_received_supply_tats: LabelValue[];
  dispatch_delivery_received_supply_graph: AppLineChartData;
  shipments_projects_inward_graph: AppLineChartData;
  mockup_graph: AppLineChartData;
  inventory_ops_graph: LabelWiseInventoryOpsDetails[];
  inventory_ops_tats: LabelValue[];
  orderlistReponse: PaginatedAPIResponse<OrdersList>;
  report: any[] | null;

  constructor() {
    this.analyticsAPI = new OpsAnalyticsDashboardAPI();
    this.response = {} as OpsAnalyticsData;
    this.orderlistReponse = {} as PaginatedAPIResponse<OrdersList>;
    this.mockup_tats = [];
    this.inward_data = [];
    this.ops_tats = [];
    this.dispatch_delivery_received_supply_tats = [
      { label: "-", value: 0, info: "" },
      { label: "-", value: 0, info: "" },
      { label: "-", value: 0, info: "" },
    ];
    this.dispatch_delivery_received_supply_graph = { labels: [], values: [] };
    this.shipments_projects_inward_graph = { labels: [], values: [] };
    this.mockup_graph = { labels: [], values: [] };
    this.prpo_tats = [{ label: "-", value: 0, info: "" }];
    this.order_list_table = [];
    this.inventory_ops_graph = [
      {
        label: "",
        raw_inward_tat: 0,
        raw_inward_tat_qc: 0,
        print_outward_tat: 0,
        print_inward_tat: 0,
        print_inward_tat_qc: 0,
      },
    ];
    this.inventory_ops_tats = [];
    this.report = null;

    makeObservable(this, {
      response: observable,
      mockup_tats: observable,
      prpo_tats: observable,
      inward_data: observable,
      ops_tats: observable,
      dispatch_delivery_received_supply_tats: observable,
      dispatch_delivery_received_supply_graph: observable,
      shipments_projects_inward_graph: observable,
      order_list_table: observable,
      orderlistReponse: observable,
      mockup_graph: observable,
      inventory_ops_graph: observable,
      inventory_ops_tats: observable,
      report: observable,
      getOrderListTableData: action,
      getAllAnalyticsData: action,
      _getOpsAnalyticsData: action,
      _getMockupTATs: action,
      _getPrPoTATs: action,
      _getDispatchDeliveryReceivedSupplyTATsData: action,
      _getDispatchDeliveryReceivedSupplyWeeklyGraphsData: action,
      _getShipmentsProjectsInwardsWeeklyGraphsData: action,
      _getInwardData: action,
      _getMockupGraph: action,
      _getInventoryOpsGraph: action,
      _getInventoryOpsTATs: action,
      _getOpsTATs: action,
      getReportData: action,
    });
  }

  getReportData = async (url: string, filters: any) => {
    try {
      this.report = await this.analyticsAPI.getReportData(url, filters);
    } catch (error) {
      console.error("Error fetching Data", error);
      this.report = [];
    }
  };

  getAllAnalyticsData = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      await Promise.all([
        this._getOpsTATs(filters),
        this._getOpsAnalyticsData(filters),
        this._getMockupTATs(filters),
        this._getInwardData(filters),
        this._getDispatchDeliveryReceivedSupplyTATsData(filters),
        this._getDispatchDeliveryReceivedSupplyWeeklyGraphsData(filters),
        this._getShipmentsProjectsInwardsWeeklyGraphsData(filters),
        this._getMockupGraph(filters),
        this._getPrPoTATs(filters),
        this._getInventoryOpsGraph(filters),
        this._getInventoryOpsTATs(filters),
      ]);
    } catch (error) {
      console.error("Error fetching Data", error);
    }
  };

  _getOpsAnalyticsData = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const response = (await this.analyticsAPI.getOpsAnalyticsData(
        "",
        filters
      )) as OpsAnalyticsData;
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      throw error;
    }
  };

  _getInwardData = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const data = (await this.analyticsAPI.getOpsAnalyticsData(
        "/tats/inward-rejections",
        filters
      )) as LabelValue[];
      runInAction(() => {
        this.inward_data = data;
      });
    } catch (error) {
      throw error;
    }
  };

  _getPrPoTATs = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const data = (await this.analyticsAPI.getOpsAnalyticsData(
        "/tats/pr-po",
        filters
      )) as LabelValue[];
      runInAction(() => {
        this.prpo_tats = data;
      });
    } catch (error) {
      throw error;
    }
  };

  _getMockupTATs = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const data = (await this.analyticsAPI.getOpsAnalyticsData(
        "/tats/mockups",
        filters
      )) as LabelValue[];
      runInAction(() => {
        this.mockup_tats = data;
      });
    } catch (error) {
      throw error;
    }
  };

  _getDispatchDeliveryReceivedSupplyTATsData = async (
    filters: OpsAnalyticsAPIFilters
  ) => {
    try {
      const data = (await this.analyticsAPI.getOpsAnalyticsData(
        "/tats/delivery-dispatch-received-supply",
        filters
      )) as LabelValue[];
      runInAction(() => {
        this.dispatch_delivery_received_supply_tats = data;
      });
    } catch (error) {
      throw error;
    }
  };

  _getDispatchDeliveryReceivedSupplyWeeklyGraphsData = async (
    filters: OpsAnalyticsAPIFilters
  ) => {
    try {
      const response = (await this.analyticsAPI.getOpsAnalyticsData(
        "/graphs/delivery-dispatch-received-supply",
        filters
      )) as AnalyticsGraphResponse[];
      runInAction(() => {
        this.dispatch_delivery_received_supply_graph =
          parseAnalyticsGraphDataToAppLineChartData(response);
      });
    } catch (error) {
      throw error;
    }
  };

  _getMockupGraph = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const response = (await this.analyticsAPI.getOpsAnalyticsData(
        "/graphs/mockups",
        filters
      )) as AnalyticsGraphResponse[];
      runInAction(() => {
        this.mockup_graph = parseAnalyticsGraphDataToAppLineChartData(response);
      });
    } catch (error) {
      throw error;
    }
  };

  _getShipmentsProjectsInwardsWeeklyGraphsData = async (
    filters: OpsAnalyticsAPIFilters
  ) => {
    try {
      const response = (await this.analyticsAPI.getOpsAnalyticsData(
        "/graphs/shipments-projects-inwards",
        filters
      )) as AnalyticsGraphResponse[];
      runInAction(() => {
        this.shipments_projects_inward_graph =
          parseAnalyticsGraphDataToAppAreaBarChartData(response);
      });
    } catch (error) {
      throw error;
    }
  };

  getOrderListTableData = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      filters.start_date = dayjs(filters.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");

      filters.end_date = dayjs(filters.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      const response = await this.analyticsAPI.getOrderTableData(filters);
      runInAction(() => {
        this.orderlistReponse = response;
      });
    } catch (error) {
      throw error;
    }
  };

  _getInventoryOpsGraph = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const response = (await this.analyticsAPI.getOpsAnalyticsData(
        "/graphs/inventory-operations",
        filters
      )) as AnalyticsGraphResponse[];
      runInAction(() => {
        this.inventory_ops_graph =
          parseAnalyticsGraphDataToAppStackChartData(response);
      });
    } catch (error) {
      throw error;
    }
  };

  _getInventoryOpsTATs = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const data = (await this.analyticsAPI.getOpsAnalyticsData(
        "/tats/inventory-operations",
        filters
      )) as LabelValue[];
      runInAction(() => {
        this.inventory_ops_tats = data;
      });
    } catch (error) {
      throw error;
    }
  };

  _getOpsTATs = async (filters: OpsAnalyticsAPIFilters) => {
    try {
      const data = (await this.analyticsAPI.getOpsAnalyticsData(
        "/tats/ops",
        filters
      )) as LabelValue[];
      runInAction(() => {
        this.ops_tats = data;
      });
    } catch (error) {
      throw error;
    }
  };
}
